import React from "react"
import { useSelector, useDispatch } from "react-redux"

import { VIEW } from "const"
import { CcRequirement } from "server/models"
import {
    StoreType,
    addModal,
} from "client/store"
import {
    Link,
    DateFormatter,
    TableColumn,
    TablePopover,
    TablePopoverData,
    UserIcon,
    NotAssigned,
    Empty,
} from "client/components"

const Columns = (editAccess: boolean) => {
    const baseRoute = useSelector((state: StoreType) => state.ccRequirement.baseRoute)
    const typeList = useSelector((state: StoreType) => state.ccRequirementType.typeList)
    const priorityList = useSelector((state: StoreType) => state.ccRequirementPriority.priorityList)
    const statusList = useSelector((state: StoreType) => state.ccRequirementStatus.statusList)
    const methodList = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodList)
    const methodIds = useSelector((state: StoreType) => state.ccRequirementReviewMethod.methodIds)
    const complianceList = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceList)
    const complianceIds = useSelector((state: StoreType) => state.ccRequirementSmartCompliance.complianceIds)

    const userList = useSelector((state: StoreType) => state.user.userList)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const requirementLink = (requirement: CcRequirement, linkContent: any) => {
        const href = `${baseRoute}/${VIEW}/${requirement.id}`
        return (
            <Link
                href={href}
                onClick={() => dispatch(addModal({ url: href }))}
            >
                {linkContent}
            </Link>
        )
    }

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
        },
        {
            field: "uniqueId",
            header: lang.uniqueId,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "250px",
            formatter: (requirement: CcRequirement) => requirementLink(requirement, requirement.uniqueId),
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            formatter: (requirement: CcRequirement) => requirementLink(requirement, requirement.name),
        },
        {
            field: "creator",
            header: lang.createdBy,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (requirement: CcRequirement) => {
                const user = userList[requirement.creator]
                return <UserIcon user={user} size="sm" />
            },
        },
        {
            field: "type",
            header: lang.type,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (requirement: CcRequirement) => {
                const type = typeList[requirement.typeId]
                return type ? type.name : <NotAssigned />
            },
        },
        {
            field: "priority",
            header: lang.priority,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (requirement: CcRequirement) => {
                const priority = priorityList[requirement.priorityId]
                return priority ? priority.name : <NotAssigned />
            },
        },
        {
            field: "status",
            header: lang.status,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (requirement: CcRequirement) => {
                const status = statusList[requirement.statusId]
                return status ? status.name : <NotAssigned />
            },
        },
        {
            field: "reviewMethods",
            header: lang.reviewMethods,
            cellMinWidth: "200px",
            formatter: (requirement: CcRequirement) => {
                if (!requirement.reviewMethodsParsed || !Object.keys(requirement.reviewMethodsParsed).length) return <Empty />

                const dataList: TablePopoverData[] = []
                methodIds.forEach((methodId) => {
                    const method = methodList[methodId]
                    if (method && requirement.reviewMethodsParsed[methodId]) {
                        dataList.push({
                            id: `${requirement.id}_${methodId}`,
                            title: method.name,
                        })
                    }
                })

                return (
                    <TablePopover
                        id={`reviewMethods${requirement.id}`}
                        dataList={dataList}
                        placement="right"
                        title={lang.reviewMethods}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "smartCompliance",
            header: lang.smartCompliance,
            cellMinWidth: "200px",
            formatter: (requirement: CcRequirement) => {
                if (!requirement.smartComplianceParsed || !Object.keys(requirement.smartComplianceParsed).length) return <Empty />

                const dataList: TablePopoverData[] = []
                complianceIds.forEach((complianceId) => {
                    const compliance = complianceList[complianceId]
                    if (compliance && requirement.smartComplianceParsed[complianceId]) {
                        dataList.push({
                            id: `${requirement.id}_${complianceId}`,
                            title: compliance.name,
                        })
                    }
                })

                return (
                    <TablePopover
                        id={`smartCompliance${requirement.id}`}
                        dataList={dataList}
                        placement="right"
                        title={lang.smartCompliance}
                        showFirst={dataList.length === 1}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "createdAt",
            header: lang.created,
            sortable: true,
            formatter: (requirement: CcRequirement) => <DateFormatter date={requirement.createdAt} />,
        },
    ]
    if (editAccess) {
        columns.push({
            field: "delete",
            centerAlign: true,
            deleteAccess: () => true,
        })
    }

    return columns
}
export default Columns
